import React from 'react';
import { XCircleIcon, UserIcon, ClockIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';

const CancelInfoDisplay = ({ 
  entity, 
  entityType = 'item', // 'item' or 'group'
  className = '',
  size = 'sm',
  fulfillmentGroup = null // For order items, pass the FG to check if we should hide this
}) => {
  const userLang = useSelector(state => state.auth.userLang);
  
  // Return null if entity is not canceled or doesn't have cancel info
  if (!entity || entity.status !== 'canceled') {
    return null;
  }

  // For order items: don't show cancel info if the fulfillment group is also canceled
  // This avoids duplication when the entire group was canceled
  if (entityType === 'item' && fulfillmentGroup?.status === 'canceled') {
    return null;
  }

  const { 
    cancelReason, 
    cancelComments, 
    canceledAt, 
    canceledBy 
  } = entity;

  // Return null if no cancel information is available
  if (!cancelReason && !cancelComments && !canceledAt && !canceledBy) {
    return null;
  }

  // Format the cancellation date
  const formattedDate = canceledAt ? new Date(canceledAt).toLocaleDateString() : null;

  // Determine who canceled based on user role/entity
  const getCanceledByText = () => {
    if (!canceledBy) return null;
    
    // For fulfillment groups, show simplified "canceled by merchant/customer" text
    if (entityType === 'group') {
      return userLang === 'zh-CN' ? '已被取消' : 'Canceled';
    }
    
    // For order items, if we get here (FG is not canceled), show more detail
    return userLang === 'zh-CN' ? `由用户取消` : `Canceled by user`;
  };

  // Get reason display text
  const getReasonText = (reason) => {
    if (!reason) return null;
    
    const reasonMap = {
      // Customer reasons
      changed_mind: userLang === 'zh-CN' ? '改变主意' : 'Changed mind',
      found_better_price: userLang === 'zh-CN' ? '找到更好的价格' : 'Found better price',
      ordered_by_mistake: userLang === 'zh-CN' ? '误下订单' : 'Ordered by mistake',
      seller_delay: userLang === 'zh-CN' ? '卖家延迟' : 'Seller delay',
      size_concerns: userLang === 'zh-CN' ? '尺寸问题' : 'Size concerns',
      color_concerns: userLang === 'zh-CN' ? '颜色问题' : 'Color concerns',
      
      // Merchant reasons
      out_of_stock: userLang === 'zh-CN' ? '缺货' : 'Out of stock',
      quality_issue: userLang === 'zh-CN' ? '质量问题' : 'Quality issue',
      damaged_inventory: userLang === 'zh-CN' ? '库存损坏' : 'Damaged inventory',
      unable_to_fulfill: userLang === 'zh-CN' ? '无法完成' : 'Unable to fulfill',
      customer_request: userLang === 'zh-CN' ? '客户要求' : 'Customer request',
      pricing_error: userLang === 'zh-CN' ? '定价错误' : 'Pricing error',
      
      // Generic
      other: userLang === 'zh-CN' ? '其他' : 'Other'
    };
    
    return reasonMap[reason] || reason;
  };

  const canceledByText = getCanceledByText();
  const reasonText = getReasonText(cancelReason);

  return (
    <div className={`bg-red-50 border border-red-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-start gap-2">
        <XCircleIcon className={`text-red-500 flex-shrink-0 ${size === 'sm' ? 'h-4 w-4 mt-0.5' : 'h-5 w-5'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium text-red-800 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
              {entityType === 'item' 
                ? (userLang === 'zh-CN' ? '物品已取消' : 'Item Canceled')
                : (userLang === 'zh-CN' ? '订单组已取消' : 'Group Canceled')
              }
            </span>
            {formattedDate && (
              <span className={`text-red-600 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                {formattedDate}
              </span>
            )}
          </div>
{/*           
          {canceledByText && (
            <div className="flex items-center gap-1 mb-1">
              <UserIcon className={`text-red-500 ${size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'}`} />
              <span className={`text-red-700 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                {canceledByText}
              </span>
            </div>
          )} */}

          {cancelReason && (
            <div className={`text-red-700 ${size === 'sm' ? 'text-xs' : 'text-sm'} mb-1`}>
              <span className="font-medium">
                {userLang === 'zh-CN' ? '原因: ' : 'Reason: '}
              </span>
              {reasonText || cancelReason}
            </div>
          )}

          {cancelComments && (
            <div className="flex items-start gap-1">
              <ChatBubbleLeftEllipsisIcon className={`text-red-500 flex-shrink-0 ${size === 'sm' ? 'h-3 w-3 mt-0.5' : 'h-4 w-4'}`} />
              <span className={`text-red-700 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                {cancelComments}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelInfoDisplay; 